import { useId, type SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";
import { Icon } from "./Icon";
import { cx } from "./cx";
import { FIELD_HEIGHT, FieldLabel, FieldNote, type FieldSize } from "./fieldParts";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: string;
  hint?: string;
  /** Error message. Replaces the hint and reddens the border. */
  error?: string;
  /** Strings or {value, label} pairs. */
  options?: Array<string | SelectOption>;
  size?: FieldSize;
}

/** Native select, styled to match Input. */
export function Select({ label, hint, error, options = [], size = "md", id, className, ...rest }: SelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const noteId = `${selectId}-note`;

  return (
    <label htmlFor={selectId} className={cx("block", className)}>
      {label && <FieldLabel required={rest.required}>{label}</FieldLabel>}
      <span className="relative block">
        <select
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? noteId : undefined}
          className={cx(
            "w-full cursor-pointer appearance-none rounded-sm border bg-surface-card pr-9 pl-3",
            error ? "border-danger-500" : "border-border-default",
            "type-body [font-size:var(--text-base)] text-text-strong outline-none inset-shadow-default",
            "[transition:var(--transition-control)] focus:border-green-700 focus:shadow-focus",
            "disabled:cursor-not-allowed disabled:bg-paper-2",
            FIELD_HEIGHT[size],
          )}
          {...rest}
        >
          {options.map((option) => {
            const { value, label: text } = typeof option === "string" ? { value: option, label: option } : option;
            return (
              <option key={value} value={value}>
                {text}
              </option>
            );
          })}
        </select>
        <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-ink-400">
          <Icon icon={ChevronDown} size={18} />
        </span>
      </span>
      <FieldNote id={noteId} error={error} hint={hint} />
    </label>
  );
}
